import { useRef } from 'react'
import { Command } from 'cmdk'
import { Icon } from './Icon'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { getCategoryColor } from '../lib/colors'
import type { Tag } from '../hooks/useTags'
import type { Composite } from '../hooks/useComposites'

// --- Inline SVG icons (14x14, Feather-style) ---
// Defaults (viewBox, stroke, strokeWidth, linecap/linejoin) live in <Icon>.
const ArchiveIcon = () => (
  <Icon><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></Icon>
)
const MailIcon = () => (
  <Icon><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></Icon>
)
const StarIcon = () => (
  <Icon><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></Icon>
)
const TagIcon = () => (
  <Icon><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></Icon>
)
const BellIcon = () => (
  <Icon><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></Icon>
)
const ClipboardIcon = () => (
  <Icon><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Icon>
)
const InboxIcon = () => (
  <Icon><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></Icon>
)
const UnreadIcon = () => (
  <Icon><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" fill="currentColor" /></Icon>
)
const DraftIcon = () => (
  <Icon><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></Icon>
)
const ComposeIcon = () => (
  <Icon><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Icon>
)
const TemplateIcon = () => (
  <Icon><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="12" y2="17" /></Icon>
)
const SlidersIcon = () => (
  <Icon><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></Icon>
)
const KeyboardIcon = () => (
  <Icon><rect x="2" y="4" width="20" height="16" rx="2" ry="2" /><line x1="6" y1="8" x2="6.01" y2="8" /><line x1="10" y1="8" x2="10.01" y2="8" /><line x1="14" y1="8" x2="14.01" y2="8" /><line x1="18" y1="8" x2="18.01" y2="8" /><line x1="6" y1="12" x2="6.01" y2="12" /><line x1="10" y1="12" x2="10.01" y2="12" /><line x1="14" y1="12" x2="14.01" y2="12" /><line x1="18" y1="12" x2="18.01" y2="12" /><line x1="8" y1="16" x2="16" y2="16" /></Icon>
)

// --- Palette item ---
function PaletteItem({ title, shortcut, icon, keywords = [], onSelect, value }: {
  title: string
  shortcut?: string
  icon: React.ReactNode
  keywords?: string[]
  onSelect: () => void
  value: string
}) {
  return (
    <Command.Item
      value={value}
      keywords={[...title.split(' '), ...keywords]}
      onSelect={onSelect}
      className="flex cursor-default items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-zinc-700 aria-selected:bg-zinc-100 aria-selected:text-zinc-900"
    >
      <span className="flex h-5 w-5 items-center justify-center text-zinc-400">{icon}</span>
      <span className="flex-1">{title}</span>
      {shortcut && <span className="text-[11px] text-zinc-400">{shortcut}</span>}
    </Command.Item>
  )
}

// --- Command groups ---
function ActionsGroup({ selectedUrn, selectedUrns, activeFilter, onClose, onArchive, onMarkRead, onStar, onTag, onRemind, onCopyConversation }: CommandPaletteProps & { onClose: () => void }) {
  if (!selectedUrn && selectedUrns.size === 0) return null
  const isArchiveView = activeFilter === 'ARCHIVE'
  return (
    <Command.Group heading="Actions">
      <PaletteItem value="archive" title={isArchiveView ? 'Unarchive' : 'Archive'} shortcut="E" icon={<ArchiveIcon />} keywords={['archive', 'remove']} onSelect={() => { onClose(); onArchive() }} />
      <PaletteItem value="mark-read" title="Mark read / unread" shortcut="U" icon={<MailIcon />} keywords={['read', 'unread']} onSelect={() => { onClose(); onMarkRead() }} />
      <PaletteItem value="star" title="Star / unstar" shortcut="S" icon={<StarIcon />} keywords={['star', 'favorite', 'bookmark']} onSelect={() => { onClose(); onStar() }} />
      <PaletteItem value="tag" title="Tag conversation" shortcut="T" icon={<TagIcon />} keywords={['tag', 'label']} onSelect={() => { onClose(); onTag() }} />
      <PaletteItem value="remind" title="Set reminder" shortcut="H" icon={<BellIcon />} keywords={['remind', 'snooze', 'later']} onSelect={() => { onClose(); onRemind() }} />
      {selectedUrn && (
        <PaletteItem value="copy" title="Copy conversation" shortcut="⌘A" icon={<ClipboardIcon />} keywords={['copy', 'clipboard', 'export']} onSelect={() => { onClose(); onCopyConversation() }} />
      )}
    </Command.Group>
  )
}

function GoToGroup({ onClose, onNavigateToFilter, onCompose, onTemplates, onOrganize }: Pick<CommandPaletteProps, 'onNavigateToFilter' | 'onCompose' | 'onTemplates' | 'onOrganize'> & { onClose: () => void }) {
  return (
    <Command.Group heading="Go to">
      <PaletteItem value="go-inbox" title="Inbox" icon={<InboxIcon />} keywords={['go', 'inbox', 'all']} onSelect={() => { onClose(); onNavigateToFilter('INBOX') }} />
      <PaletteItem value="go-unread" title="Unread" icon={<UnreadIcon />} keywords={['go', 'unread', 'inbox']} onSelect={() => { onClose(); onNavigateToFilter('UNREAD') }} />
      <PaletteItem value="go-waiting-theirs" title="Waiting for their reply" icon={<UnreadIcon />} keywords={['waiting', 'their', 'reply', 'sent', 'inbox']} onSelect={() => { onClose(); onNavigateToFilter('WAITING_THEIRS') }} />
      <PaletteItem value="go-waiting-mine" title="Waiting for my reply" icon={<UnreadIcon />} keywords={['waiting', 'my', 'reply', 'reply', 'inbox']} onSelect={() => { onClose(); onNavigateToFilter('WAITING_MINE') }} />
      <PaletteItem value="go-starred" title="Starred" icon={<StarIcon />} keywords={['go', 'starred', 'bookmarks']} onSelect={() => { onClose(); onNavigateToFilter('STARRED') }} />
      <PaletteItem value="go-archive" title="Archive" icon={<ArchiveIcon />} keywords={['go', 'archive']} onSelect={() => { onClose(); onNavigateToFilter('ARCHIVE') }} />
      <PaletteItem value="go-drafts" title="Drafts" icon={<DraftIcon />} keywords={['go', 'drafts']} onSelect={() => { onClose(); onNavigateToFilter('DRAFTS') }} />
      <PaletteItem value="compose" title="Compose new message" shortcut="C" icon={<ComposeIcon />} keywords={['compose', 'new', 'write', 'message']} onSelect={() => { onClose(); onCompose() }} />
      <PaletteItem value="templates" title="Snippets / Templates" icon={<TemplateIcon />} keywords={['snippets', 'templates', 'quick', 'reply']} onSelect={() => { onClose(); onTemplates() }} />
      <PaletteItem value="organize" title="Organize sidebar" shortcut="L" icon={<SlidersIcon />} keywords={['organize', 'settings', 'sidebar', 'customize']} onSelect={() => { onClose(); onOrganize() }} />
    </Command.Group>
  )
}

// Contextual: Tag with <tag name> (kondo-parity 1.7). Only rendered
// when a conversation is selected, because tagging requires a target.
// Each tag becomes its own palette entry so the user can directly
// toggle assignment via `/` → "tag hot" → Enter without the two-
// click TagPicker flow.
function TagWithGroup({
  onClose,
  selectedUrn,
  tags,
  getTagsForConversation,
  onAssignTag,
  onRemoveTag,
}: {
  onClose: () => void
  selectedUrn: string | null
  tags: Tag[]
  getTagsForConversation: (urn: string) => string[]
  onAssignTag: (tagId: string, urn: string) => void
  onRemoveTag: (tagId: string, urn: string) => void
}) {
  if (!selectedUrn || tags.length === 0) return null
  const assigned = new Set(getTagsForConversation(selectedUrn))
  return (
    <Command.Group heading="Tag with">
      {tags.map((tag) => {
        const isAssigned = assigned.has(tag.id)
        return (
          <PaletteItem
            key={tag.id}
            value={`tag-${tag.id}`}
            title={`${isAssigned ? 'Remove' : 'Apply'}: ${tag.name}`}
            keywords={['tag', 'label', tag.name]}
            icon={
              tag.icon ? (
                <span className="text-[14px] leading-none" aria-hidden="true">{tag.icon}</span>
              ) : (
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: getCategoryColor(tag.color) }} />
              )
            }
            onSelect={() => {
              onClose()
              if (isAssigned) onRemoveTag(tag.id, selectedUrn)
              else onAssignTag(tag.id, selectedUrn)
            }}
          />
        )
      })}
    </Command.Group>
  )
}

// Saved filters (Inbox Composites, ADR 0029) surfaced as palette
// entries. Users can `/` → "hot investors" → Enter and land on the
// saved filter view without touching the sidebar.
function FiltersGroup({
  onClose,
  composites,
  onNavigateToFilter,
}: {
  onClose: () => void
  composites: Composite[]
  onNavigateToFilter: (filter: string) => void
}) {
  if (composites.length === 0) return null
  return (
    <Command.Group heading="Open filter">
      {composites.map((composite) => (
        <PaletteItem
          key={composite.id}
          value={`composite-${composite.id}`}
          title={composite.name}
          keywords={['filter', 'view', 'saved', composite.name]}
          icon={
            composite.icon ? (
              <span className="text-[14px] leading-none" aria-hidden="true">{composite.icon}</span>
            ) : (
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: getCategoryColor(composite.color) }} />
            )
          }
          onSelect={() => {
            onClose()
            onNavigateToFilter(`composite:${composite.id}`)
          }}
        />
      ))}
    </Command.Group>
  )
}

function HelpGroup({ onClose, onShowShortcuts }: Pick<CommandPaletteProps, 'onShowShortcuts'> & { onClose: () => void }) {
  return (
    <Command.Group heading="Help">
      <PaletteItem value="shortcuts" title="Keyboard shortcuts" shortcut="?" icon={<KeyboardIcon />} keywords={['shortcuts', 'keys', 'help']} onSelect={() => { onClose(); onShowShortcuts() }} />
    </Command.Group>
  )
}

// --- Main component ---
interface CommandPaletteProps {
  onClose: () => void
  selectedUrn: string | null
  selectedUrns: Set<string>
  activeFilter: string
  onNavigateToFilter: (filter: string) => void
  onArchive: () => void
  onMarkRead: () => void
  onStar: () => void
  onTag: () => void
  onRemind: () => void
  onCompose: () => void
  onCopyConversation: () => void
  onOrganize: () => void
  onTemplates: () => void
  onShowShortcuts: () => void
  // ─── Contextual content (kondo-parity 1.7) ────────────────────────────────
  /** All of the user's tags. Rendered as "Tag with:" entries when a
   * conversation is selected. */
  tags?: Tag[]
  /** Returns tag ids currently assigned to a given conversation. Used to
   * toggle between Apply/Remove in the palette label. */
  getTagsForConversation?: (urn: string) => string[]
  onAssignTag?: (tagId: string, urn: string) => void
  onRemoveTagAssignment?: (tagId: string, urn: string) => void
  /** Saved Inbox Composites (ADR 0029). Rendered as "Open filter:" entries. */
  composites?: Composite[]
}

export default function CommandPalette({ onClose, ...props }: CommandPaletteProps) {
  // Tab-trap the palette so keyboard-only users can't inadvertently escape
  // the dialog into the backing page (same pattern as the other modals —
  // OrganizeModal, ShortcutsHelpPanel, EmojiPicker, GifPicker).
  const panelRef = useRef<HTMLDivElement | null>(null)
  useFocusTrap(panelRef)
  return (
    <div
      className="animate-fade-in-fast fixed inset-0 z-50 flex items-start justify-center bg-black/20 pt-[20vh]"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        ref={panelRef}
        className="animate-scale-in w-[520px] overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.15)]"
        onClick={(e) => e.stopPropagation()}
      >
        <Command
          filter={(value, search, keywords = []) =>
            keywords.concat(value).join(' ').toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0
          }
          loop
        >
          <Command.Input
            autoFocus
            placeholder="Type a command..."
            className="w-full border-b border-zinc-100 px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none"
          />
          <Command.List className="max-h-[320px] overflow-y-auto p-1.5 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-zinc-400">
            <Command.Empty className="py-6 text-center text-xs text-zinc-400">
              No matching commands
            </Command.Empty>
            <ActionsGroup {...props} onClose={onClose} />
            {props.tags && props.getTagsForConversation && props.onAssignTag && props.onRemoveTagAssignment && (
              <TagWithGroup
                onClose={onClose}
                selectedUrn={props.selectedUrn}
                tags={props.tags}
                getTagsForConversation={props.getTagsForConversation}
                onAssignTag={props.onAssignTag}
                onRemoveTag={props.onRemoveTagAssignment}
              />
            )}
            {props.composites && (
              <FiltersGroup
                onClose={onClose}
                composites={props.composites}
                onNavigateToFilter={props.onNavigateToFilter}
              />
            )}
            <GoToGroup onClose={onClose} onNavigateToFilter={props.onNavigateToFilter} onCompose={props.onCompose} onTemplates={props.onTemplates} onOrganize={props.onOrganize} />
            <HelpGroup onClose={onClose} onShowShortcuts={props.onShowShortcuts} />
          </Command.List>
        </Command>
      </div>
    </div>
  )
}
